/**
 * fathom doctor — Check project health and configuration.
 *
 * Validates: Node version, .fathom/ directory, config.json, intent.yaml, data files.
 * Each check prints a checkmark or cross with an actionable fix message.
 * Exits 0 if all pass, 1 if any fail.
 */
import { Command } from "commander";
import { existsSync, readFileSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import yaml from "js-yaml";
import { loadData } from "@fathom/core";
import { warnAction } from "../ux/errors.js";

interface CheckResult {
  name: string;
  ok: boolean;
  message: string;
}

/**
 * Run all doctor checks against a given working directory.
 * Exported for testability.
 */
export async function runChecks(cwd: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  // 1. Node version >= 20
  const nodeMajor = parseInt(process.versions.node.split(".")[0], 10);
  results.push({
    name: "Node version >= 20",
    ok: nodeMajor >= 20,
    message: nodeMajor >= 20
      ? `Node.js v${process.versions.node}`
      : "Upgrade to Node.js 20 or later",
  });

  // 2. .fathom/ directory exists
  const fathomDir = resolve(cwd, ".fathom");
  const hasFathomDir = existsSync(fathomDir);
  results.push({
    name: ".fathom/ directory",
    ok: hasFathomDir,
    message: hasFathomDir
      ? ".fathom/ found"
      : "Run `fathom init` to create project config",
  });

  // 3. config.json is valid JSON
  const configPath = resolve(fathomDir, "config.json");
  let configOk = false;
  let configMsg = "Run `fathom init` to create project config";
  if (hasFathomDir && existsSync(configPath)) {
    try {
      JSON.parse(readFileSync(configPath, "utf-8"));
      configOk = true;
      configMsg = "config.json valid";
    } catch {
      configMsg = "Delete .fathom/config.json and run `fathom init`";
    }
  } else if (hasFathomDir) {
    configMsg = "Run `fathom init` to create config.json";
  }
  results.push({
    name: "config.json valid",
    ok: configOk,
    message: configMsg,
  });

  // 4. intent.yaml parseable
  const intentPath = resolve(fathomDir, "intent.yaml");
  let intentOk = false;
  let intentMsg = "Run `fathom init` to create intent.yaml";
  if (hasFathomDir && existsSync(intentPath)) {
    try {
      yaml.load(readFileSync(intentPath, "utf-8"), { schema: yaml.JSON_SCHEMA });
      intentOk = true;
      intentMsg = "intent.yaml parseable";
    } catch {
      intentMsg = "Check intent.yaml syntax — run `fathom validate`";
    }
  }
  results.push({
    name: "intent.yaml parseable",
    ok: intentOk,
    message: intentMsg,
  });

  // 5. Data files loadable
  let dataOk = false;
  let dataMsg = "Reinstall fathom — `npm i -g fathom`";
  try {
    loadData();
    dataOk = true;
    dataMsg = "Core data files loaded";
  } catch {
    // keep default fix message
  }
  results.push({
    name: "Data files loadable",
    ok: dataOk,
    message: dataMsg,
  });

  return results;
}

export const doctorCommand = new Command("doctor")
  .description("Check project health and configuration")
  .action(async () => {
    console.log(chalk.bold("\nFathom — Doctor"));
    console.log(chalk.dim("\u2500".repeat(50)));

    const results = await runChecks(process.cwd());

    let passed = 0;
    for (const r of results) {
      if (r.ok) {
        console.log(chalk.green(`  \u2713 ${r.name}`));
        passed++;
      } else {
        console.log(chalk.red(`  \u2717 ${r.name} \u2014 ${r.message}`));
      }
    }

    console.log(
      chalk.dim(`\n${passed}/${results.length} checks passed\n`),
    );

    if (passed < results.length) {
      process.exit(1);
    }
  });
